import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Dashboard from './admin/Dashboard';
import Login from './admin/Login';
import ClientDetail from './admin/clients/ClientDetail';
import ClientsList from './admin/clients/ClientsList';
import MessagesInbox from './admin/messages/MessagesInbox';
import SettingsLayout from './admin/settings/SettingsLayout';
import Schedule from './admin/schedule/Schedule';
import JobsList from './admin/jobs/JobsList';
import JobsNew from './admin/jobs/JobsNew';
import AdminJobDetail from './admin/jobs/JobDetail';
import JobEdit from './admin/jobs/JobEdit';
import QuotesList from './admin/quotes/QuotesList';
import QuoteForm from './admin/quotes/QuoteForm';
import QuoteDetail from './admin/quotes/QuoteDetail';
import InvoiceForm from './admin/invoices/InvoiceForm';
import CertificatesOverview from './admin/certificates/CertificatesOverview';
import ReviewRequests from './admin/reviews/ReviewRequests';
import SignupsList from './admin/signups/SignupsList';
import AdminJobSheets from './admin/engineer/JobSheets';
import AdminEngineerFiles from './admin/engineer/Files';
import AdminEngineerParts from './admin/engineer/Parts';
import AdminEngineerFaults from './admin/engineer/Faults';
import AdminEngineerExpenses from './admin/engineer/Expenses';
import AdminEngineerMapping from './admin/engineer/Mapping';
import Chat from './portal/Chat';
import DashboardPortal from './portal/Dashboard';
import Documents from './portal/Documents';
import RequestJobPage from './portal/RequestJobPage';
import History from './portal/History';
import InvoiceDetail from './portal/InvoiceDetail';
import Invoices from './portal/Invoices';
import JobDetail from './portal/JobDetail';
import KeyDates from './portal/KeyDates';
import PortalAuthGate from './portal/PortalAuthGate';
import PortalLogin from './portal/Login';
import BillingDocs from './portal/BillingDocs';
import Quotes from './portal/Quotes';
import QuoteDetailPortal from './portal/QuoteDetail';
import PaymentStatus from './portal/PaymentStatus';
import PublicInvoiceView from './portal/PublicInvoiceView';
import UnifiedVerify from './shared/UnifiedVerify';
import AdminShell from './shared/layout/AdminShell';
import EngineerShell from './engineer/EngineerShell';
import EngineerAuthGate from './engineer/EngineerAuthGate';
import EngineerLogin from './engineer/EngineerLogin';
import EngineerDashboard from './engineer/Dashboard';
import EngineerJobSheets from './engineer/Jobs';
import EngineerJobSheetNew from './engineer/JobSheetNew';
import EngineerJobSheetDetail from './engineer/JobSheetDetail';
import EngineerMapping from './engineer/Mapping';
import EngineerCertificates from './engineer/Certificates';
import EngineerCertificateDetail from './engineer/CertificateDetail';
import EngineerSignatures from './engineer/Signatures';
import EngineerFiles from './engineer/Files';
import EngineerMessages from './engineer/Messages';
import EngineerParts from './engineer/Parts';
import EngineerPartsDetail from './engineer/PartsDetail';
import EngineerFaults from './engineer/Faults';
import EngineerFaultsDetail from './engineer/FaultsDetail';
import EngineerExpenses from './engineer/Expenses';
import MarketingLayout from './marketing/MarketingLayout';
import MarketingHome from './marketing/Home';
import MarketingFeatures from './marketing/Features';
import MarketingPricing from './marketing/Pricing';
import MarketingSignUp from './marketing/SignUp';
import { TermsOfService, PrivacyPolicy } from './marketing/Legal';

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing pages (/, /features, /pricing, /sign-up) — outside
          every auth gate. main.jsx folds hashless URLs (e.g. /pricing) into
          the hash so both /pricing and /#/pricing resolve here. */}
      <Route path="/" element={<MarketingLayout />}>
        <Route index element={<MarketingHome />} />
        <Route path="features" element={<MarketingFeatures />} />
        <Route path="pricing" element={<MarketingPricing />} />
        <Route path="sign-up" element={<MarketingSignUp />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
      </Route>

      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<ClientsList />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="messages" element={<MessagesInbox />} />
        <Route path="quotes" element={<QuotesList />} />
        <Route path="quotes/new" element={<QuoteForm />} />
        <Route path="quotes/:quoteId" element={<QuoteDetail />} />
        <Route path="quotes/:quoteId/edit" element={<QuoteForm />} />
        <Route path="invoices/new" element={<InvoiceForm />} />
        <Route path="invoices/:invoiceId/edit" element={<InvoiceForm />} />
        <Route path="certificates" element={<CertificatesOverview />} />
        <Route path="reviews" element={<ReviewRequests />} />
        <Route path="signups" element={<SignupsList />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="jobs" element={<JobsList />} />
        <Route path="jobs/new" element={<JobsNew />} />
        <Route path="jobs/:id" element={<AdminJobDetail />} />
        <Route path="jobs/:id/edit" element={<JobEdit />} />
        <Route path="settings" element={<SettingsLayout />} />
        <Route path="engineer/job-sheets" element={<AdminJobSheets />} />
        <Route path="engineer/files" element={<AdminEngineerFiles />} />
        <Route path="engineer/parts" element={<AdminEngineerParts />} />
        <Route path="engineer/faults" element={<AdminEngineerFaults />} />
        <Route path="engineer/expenses" element={<AdminEngineerExpenses />} />
        <Route path="engineer/mapping" element={<AdminEngineerMapping />} />
      </Route>

      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/login/:tenantSlug" element={<PortalLogin />} />
      {/* Unified OTP magic-link exchange — handles all portal types */}
      <Route path="/portal/verify" element={<UnifiedVerify />} />
      {/* Stripe Checkout return URLs (pass 6 §6E) — outside the auth gate;
          the portal session survives the round-trip. */}
      <Route path="/portal/invoice-paid/:invoiceId" element={<PaymentStatus paid />} />
      <Route path="/portal/payment-cancelled/:invoiceId" element={<PaymentStatus paid={false} />} />
      {/* Public invoice view (share link, unauthenticated) — outside auth gate. */}
      <Route path="/i/:token" element={<PublicInvoiceView />} />
      <Route path="/portal/:tenantSlug/:clientToken" element={<PortalAuthGate />}>
        <Route index element={<DashboardPortal />} />
        <Route path="request-job" element={<RequestJobPage />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="estimates" element={<BillingDocs docType="estimate" />} />
        <Route path="estimates/:invoiceId" element={<InvoiceDetail />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="quotes/:quoteId" element={<QuoteDetailPortal />} />
        <Route path="history" element={<History />} />
        <Route path="history/:jobId" element={<JobDetail />} />
        <Route path="keydates" element={<KeyDates />} />
        <Route path="documents" element={<Documents />} />
        <Route path="chat" element={<Chat />} />
      </Route>
      <Route path="/portal/:tenantSlug" element={<PortalAuthGate />}>
        <Route index element={<DashboardPortal />} />
        <Route path="request-job" element={<RequestJobPage />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="estimates" element={<BillingDocs docType="estimate" />} />
        <Route path="estimates/:invoiceId" element={<InvoiceDetail />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="quotes/:quoteId" element={<QuoteDetailPortal />} />
        <Route path="history" element={<History />} />
        <Route path="history/:jobId" element={<JobDetail />} />
        <Route path="keydates" element={<KeyDates />} />
        <Route path="documents" element={<Documents />} />
        <Route path="chat" element={<Chat />} />
      </Route>

      {/* Engineer Portal Routes */}
      <Route path="/engineer/login" element={<EngineerLogin />} />
      <Route path="/engineer/login/:tenantSlug" element={<EngineerLogin />} />
      {/* Session route (no slug) — EngineerAuthGate resolves an existing
          engineer session or bounces to /engineer/login. Without this,
          /engineer fell through to the global catch-all → /admin. */}
      <Route path="/engineer" element={<EngineerAuthGate />}>
        <Route index element={<EngineerDashboard />} />
        <Route path="jobs" element={<EngineerJobSheets />} />
        <Route path="jobs/new" element={<EngineerJobSheetNew />} />
        <Route path="jobs/:id" element={<EngineerJobSheetDetail />} />
        <Route path="certificates" element={<EngineerCertificates />} />
        <Route path="certificates/:id" element={<EngineerCertificateDetail />} />
        <Route path="signatures" element={<EngineerSignatures />} />
        <Route path="files" element={<EngineerFiles />} />
        <Route path="mapping" element={<EngineerMapping />} />
        <Route path="messages" element={<EngineerMessages />} />
        <Route path="parts" element={<EngineerParts />} />
        <Route path="parts/:id" element={<EngineerPartsDetail />} />
        <Route path="faults" element={<EngineerFaults />} />
        <Route path="faults/:id" element={<EngineerFaultsDetail />} />
        <Route path="expenses" element={<EngineerExpenses />} />
      </Route>
      <Route path="/engineer/:tenantSlug" element={<EngineerAuthGate />}>
        <Route index element={<EngineerDashboard />} />
        <Route path="jobs" element={<EngineerJobSheets />} />
        <Route path="jobs/new" element={<EngineerJobSheetNew />} />
        <Route path="jobs/:id" element={<EngineerJobSheetDetail />} />
        <Route path="certificates" element={<EngineerCertificates />} />
        <Route path="certificates/:id" element={<EngineerCertificateDetail />} />
        <Route path="signatures" element={<EngineerSignatures />} />
        <Route path="files" element={<EngineerFiles />} />
        <Route path="mapping" element={<EngineerMapping />} />
        <Route path="messages" element={<EngineerMessages />} />
        <Route path="parts" element={<EngineerParts />} />
        <Route path="parts/:id" element={<EngineerPartsDetail />} />
        <Route path="faults" element={<EngineerFaults />} />
        <Route path="faults/:id" element={<EngineerFaultsDetail />} />
        <Route path="expenses" element={<EngineerExpenses />} />
      </Route>
      {/* Any other engineer path (hashless-entry redirects, typos) must
          never fall through to /admin — send it to the engineer login. */}
      <Route path="/engineer/*" element={<Navigate to="/engineer/login" replace />} />

      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
